
import { supabase } from '@/integrations/supabase/client';
import { Procurement } from '@/types/database.types';
import { enrichProcurementData } from './dataTransformationService';
import { getSourceNamesInBatch } from './batchOperationsService';

/**
 * Finance-specific query optimized for pending approvals
 */
export const getFinanceProcurements = async (
  statuses: string[] = ['pending_finance', 'pending', 'initiated', 'awaiting_approval'],
  limit: number = 25,
  offset: number = 0
): Promise<{ data: Procurement[]; count: number }> => {
  console.log('Fetching finance procurements with simplified query', { statuses, limit, offset });
  
  try {
    // Get count for pagination
    const { count } = await supabase
      .from('procurements')
      .select('*', { count: 'exact', head: true })
      .in('status', statuses);

    // Get paginated data with only essential related data for performance
    const { data, error } = await supabase
      .from('procurements')
      .select(`
        id,
        status,
        created_at,
        updated_at,
        source_type,
        source_id,
        requestor_id,
        product_name,
        total_quantity,
        total_amount,
        expected_delivery_date,
        notes,
        costs:procurement_costs(
          price_per_unit,
          transport_cost_per_unit,
          loading_unloading_cost_per_unit,
          agent_cost_per_unit,
          other_cost_per_unit,
          vat_applicable
        ),
        finance:procurement_finance(
          payment_term_type,
          payment_term_days,
          payment_due_date,
          approved_by,
          finance_approved_at
        ),
        quality:procurement_quality(
          grading_type,
          damage_kg,
          compensation_kg,
          damage_percentage,
          preliminary_damage_kg,
          preliminary_compensation_kg,
          preliminary_damage_percentage,
          preliminary_graded_at,
          final_grading_expected_date,
          final_graded_at,
          is_final_grading_complete,
          quality_grade,
          sample_size_checked,
          damage_notes,
          preliminary_notes
        ),
        warehouse:procurement_warehouse(
          id,
          warehouse_confirmed_by,
          warehouse_confirmation_date,
          net_kg_payable,
          damage_percentage,
          sample_taken,
          inspection_notes
        )
      `)
      .in('status', statuses)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error("Error getting finance procurements:", error);
      throw error;
    }

    console.log('Raw finance procurements fetched:', data?.length || 0);
    
    // Manually fetch user profiles for requestors with better error handling
    const requestorIds = data?.map(p => p.requestor_id).filter(Boolean) || [];
    console.log('Requestor IDs to fetch:', requestorIds);
    
    let userProfiles: any[] = [];
    if (requestorIds.length > 0) {
      try {
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('id, first_name, last_name, role, created_at, updated_at, status')
          .in('id', requestorIds);
          
        if (profilesError) {
          console.error('Error fetching user profiles:', profilesError);
          // Continue without profiles rather than failing
        } else if (profiles && profiles.length > 0) {
          userProfiles = profiles;
          console.log('Successfully fetched user profiles:', userProfiles.length);
          console.log('User profiles data:', userProfiles.map(p => ({ 
            id: p.id, 
            name: `${p.first_name} ${p.last_name}`,
            role: p.role 
          })));
        } else {
          console.warn('No user profiles found for requestor IDs:', requestorIds);
        }
      } catch (profileError) {
        console.error('Exception while fetching user profiles:', profileError);
        // Continue without profiles
      }
    }
    
    // Merge the data manually with improved matching
    const mergedData = data?.map(procurement => {
      const matchingProfile = userProfiles.find(profile => profile.id === procurement.requestor_id);
      
      console.log(`Procurement ${procurement.id.substring(0, 8)}:`, {
        requestor_id: procurement.requestor_id,
        found_profile: !!matchingProfile,
        profile_name: matchingProfile ? `${matchingProfile.first_name} ${matchingProfile.last_name}` : 'Not found'
      });
      
      return {
        ...procurement,
        requestor: matchingProfile || null
      };
    }) || [];
    
    console.log('Sample merged data with requestor:', {
      id: mergedData[0]?.id,
      requestor_id: mergedData[0]?.requestor_id,
      requestor: mergedData[0]?.requestor,
      requestor_name: mergedData[0]?.requestor ? 
        `${mergedData[0].requestor.first_name} ${mergedData[0].requestor.last_name}` : 
        'No requestor'
    });
    
    // Batch fetch source names
    const sourceNames = await getSourceNamesInBatch(mergedData);
    
    // Enrich and return data
    const enrichedData = await enrichProcurementData(mergedData, sourceNames);
    
    console.log('Finance procurements processed successfully:', enrichedData.length);
    console.log('Sample enriched requestor data:', {
      id: enrichedData[0]?.id,
      requestor: enrichedData[0]?.requestor,
      requestor_name: enrichedData[0]?.requestor ? 
        `${enrichedData[0].requestor.first_name} ${enrichedData[0].requestor.last_name}` : 
        'No requestor after enrichment'
    });
    
    return {
      data: enrichedData,
      count: count || 0
    };
  } catch (error) {
    console.error("Error in getFinanceProcurements:", error);
    throw error;
  }
};

/**
 * Get approved finance procurements (pending_warehouse status)
 */
export const getApprovedFinanceProcurements = async (
  limit: number = 25,
  offset: number = 0
): Promise<{ data: Procurement[]; count: number }> => {
  return getFinanceProcurements(['pending_warehouse', 'approved'], limit, offset);
};

/**
 * Get rejected finance procurements
 */
export const getRejectedFinanceProcurements = async (
  limit: number = 25,
  offset: number = 0
): Promise<{ data: Procurement[]; count: number }> => {
  console.log('Fetching rejected finance procurements');
  
  try {
    // Get count
    const { count } = await supabase
      .from('procurements')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'rejected')
      .eq('rejection_source', 'finance');

    // Get data with related data (excluding requestor for now)
    const { data, error } = await supabase
      .from('procurements')
      .select(`
        *,
        costs:procurement_costs(*),
        finance:procurement_finance(*),
        warehouse:procurement_warehouse(*),
        items:procurement_items(*),
        audit_trail:procurement_audit(*),
        quality:procurement_quality(
          grading_type,
          damage_kg,
          compensation_kg,
          damage_percentage,
          preliminary_damage_kg,
          preliminary_compensation_kg,
          preliminary_damage_percentage,
          preliminary_graded_at,
          final_grading_expected_date,
          final_graded_at,
          is_final_grading_complete,
          quality_grade,
          damage_notes,
          preliminary_notes
        )
      `)
      .eq('status', 'rejected')
      .eq('rejection_source', 'finance')
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error("Error getting rejected finance procurements:", error);
      throw error;
    }

    console.log('Raw rejected finance procurements fetched:', data?.length || 0);

    // Manually fetch user profiles for requestors
    const requestorIds = data?.map(p => p.requestor_id).filter(Boolean) || [];
    let userProfiles: any[] = [];
    
    if (requestorIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, first_name, last_name, role')
        .in('id', requestorIds);
        
      if (!profilesError && profiles) {
        userProfiles = profiles;
      } else {
        console.error('Error fetching user profiles for rejected:', profilesError);
      }
    }
    
    // Merge the data manually
    const mergedData = data?.map(procurement => ({
      ...procurement,
      requestor: userProfiles.find(profile => profile.id === procurement.requestor_id) || null
    })) || [];
    
    console.log('Sample rejected data with requestor:', {
      id: mergedData[0]?.id,
      requestor_id: mergedData[0]?.requestor_id,
      requestor: mergedData[0]?.requestor
    });
    
    // Batch fetch source names
    const sourceNames = await getSourceNamesInBatch(mergedData);
    
    // Enrich and return data
    const enrichedData = await enrichProcurementData(mergedData, sourceNames);
    
    return {
      data: enrichedData,
      count: count || 0
    };
  } catch (error) {
    console.error("Error in getRejectedFinanceProcurements:", error);
    throw error;
  }
};
